function recordRequestLatency(_elapsedMs: number, _ok: boolean): void {}

const NETWORK_TIMEOUT_CODE = 'NETWORK_TIMEOUT';
const NETWORK_FAILURE_CODE = 'NETWORK_FAILURE';

const NETWORK_FAILURE_PATTERNS = [
  'failed to fetch',
  'network request failed',
  'load failed',
  'networkerror',
  'the network connection was lost',
  'internet connection appears to be offline',
  // Our own createTimedFetch abort, and the browser/undici wording for the
  // same thing. These used to fall through as "not a network failure", which
  // is how a 20s timeout on a weak connection ended up being treated as a
  // real authentication failure and signed the user out.
  'the request timed out',
  'timed out',
  'timeout',
  'aborted',
  'signal is aborted',
  'connection closed',
  'connection reset',
  'err_network',
  'err_internet_disconnected',
  'err_connection',
  'fetch failed',
];

export const SUPABASE_REQUEST_TIMEOUT_MS = 20000;

export class NetworkRequestTimeoutError extends Error {
  code = NETWORK_TIMEOUT_CODE;
  retryable = true;
  timeoutMs: number;

  constructor(timeoutMs: number) {
    super(`The request timed out after ${Math.round(timeoutMs / 1000)} seconds.`);
    this.name = 'NetworkRequestTimeoutError';
    this.timeoutMs = timeoutMs;
  }
}

export class NetworkConnectionError extends Error {
  code = NETWORK_FAILURE_CODE;
  retryable = true;

  constructor(message = 'The network connection failed while loading data.') {
    super(message);
    this.name = 'NetworkConnectionError';
  }
}

export function isLikelyNetworkFailure(error: unknown) {
  if (error instanceof NetworkRequestTimeoutError || error instanceof NetworkConnectionError) {
    return true;
  }

  if (error instanceof Error && (error.name === 'AbortError' || error.name === 'TypeError')) {
    return true;
  }

  const code =
    typeof error === 'object' && error !== null
      ? String((error as { code?: unknown }).code || '')
      : '';

  if (code === NETWORK_TIMEOUT_CODE || code === NETWORK_FAILURE_CODE) {
    return true;
  }

  const message = error instanceof Error ? error.message.toLowerCase() : String(error || '').toLowerCase();
  return NETWORK_FAILURE_PATTERNS.some((pattern) => message.includes(pattern));
}

/**
 * True when an error means "we could not reach the server right now", as opposed
 * to "the server answered and said no".
 *
 * Anything that gates *authentication* decisions must use this and never
 * `isLikelyNetworkFailure` alone: a request that never reached PostgREST, a
 * request the gateway rejected (5xx), and a rate-limited request all leave the
 * session perfectly valid, so none of them may be treated as a sign-out.
 */
export function isTransientConnectivityError(error: unknown) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (isLikelyNetworkFailure(error)) return true;

  const status = getStatusCode(error);
  if (status === 0 || status === 408 || status === 425 || status === 429 || status >= 500) {
    return true;
  }

  // PostgREST/Supabase surface connectivity problems as untyped objects with a
  // message but no HTTP status. Treat "no status at all" as transient too , a
  // definitive rejection always carries one.
  if (
    typeof error === 'object' &&
    error !== null &&
    Number.isNaN(status) &&
    !('code' in error)
  ) {
    return true;
  }

  return false;
}

export function getStatusCode(error: unknown) {
  const rawStatus =
    typeof error === 'object' && error !== null
      ? (error as { status?: unknown; statusCode?: unknown }).status
        ?? (error as { status?: unknown; statusCode?: unknown }).statusCode
      : undefined;

  return typeof rawStatus === 'number' ? rawStatus : Number.NaN;
}

export function isRetryableQueryError(error: unknown) {
  if (error instanceof NetworkRequestTimeoutError || error instanceof NetworkConnectionError) {
    return true;
  }

  const code =
    typeof error === 'object' && error !== null
      ? String((error as { code?: unknown }).code || '')
      : '';

  if (code === NETWORK_TIMEOUT_CODE || code === NETWORK_FAILURE_CODE) {
    return true;
  }

  const status = getStatusCode(error);
  if (status === 408 || status === 425 || status === 429 || status >= 500) {
    return true;
  }

  if (error instanceof Error && error.name === 'AbortError') {
    return true;
  }

  return isLikelyNetworkFailure(error);
}

export function getQueryRetryDelay(attemptIndex: number) {
  return Math.min(1000 * 2 ** attemptIndex, 4000);
}

export function getFriendlyQueryErrorMessage(
  error: unknown,
  fallback = 'We could not load this data right now.',
) {
  if (error instanceof NetworkRequestTimeoutError) {
    return 'This request is taking too long. Check your network and try again.';
  }

  if (error instanceof NetworkConnectionError || isLikelyNetworkFailure(error)) {
    return 'The connection dropped while loading this page. Please retry.';
  }

  const status = getStatusCode(error);
  if (status === 429) {
    return 'Too many requests were sent at once. Please wait a moment and retry.';
  }

  if (status >= 500) {
    return 'The server had trouble responding. Please try again in a moment.';
  }

  return fallback;
}

function createTimeoutSignal(timeoutMs: number, originalSignal?: AbortSignal | null) {
  const controller = new AbortController();
  const timeoutError = new NetworkRequestTimeoutError(timeoutMs);
  const timeoutId = globalThis.setTimeout(() => {
    controller.abort(timeoutError);
  }, timeoutMs);

  const handleAbort = () => {
    controller.abort(originalSignal?.reason);
  };

  if (originalSignal) {
    if (originalSignal.aborted) {
      handleAbort();
    } else {
      originalSignal.addEventListener('abort', handleAbort, { once: true });
    }
  }

  const cleanup = () => {
    globalThis.clearTimeout(timeoutId);
    if (originalSignal) {
      originalSignal.removeEventListener('abort', handleAbort);
    }
  };

  return {
    signal: controller.signal,
    timeoutError,
    cleanup,
  };
}

export function createTimedFetch(timeoutMs = SUPABASE_REQUEST_TIMEOUT_MS): typeof fetch {
  return async (input, init = {}) => {
    const { signal, timeoutError, cleanup } = createTimeoutSignal(timeoutMs, init.signal);
    const startedAt = Date.now();

    try {
      const response = await fetch(input, {
        ...init,
        signal,
      });
      recordRequestLatency(Date.now() - startedAt, true);
      return response;
    } catch (error) {
      recordRequestLatency(Date.now() - startedAt, false);
      if (signal.aborted && signal.reason === timeoutError) {
        throw timeoutError;
      }

      if (isLikelyNetworkFailure(error)) {
        throw new NetworkConnectionError();
      }

      throw error;
    } finally {
      cleanup();
    }
  };
}

/**
 * Give a promise a deadline its own client never gave it.
 *
 * `createTimedFetch` bounds every *request*, but it cannot bound the awaits the
 * Supabase auth client performs around them: `GoTrueClient._acquireLock` is
 * called with an acquire timeout of `-1`, i.e. wait forever. A `getSession()`
 * queued behind a held auth lock therefore never settles, and neither does
 * anything awaiting it , the deadlock documented at the top of
 * `handleAuthStateChange` in `contexts/AuthContext.tsx`, where "no request is
 * ever sent".
 *
 * A promise that never settles is the dangerous shape, because callers cannot
 * `catch` it: their own `finally` blocks, which re-enable buttons and clear
 * in-flight caches, never run. This converts that into an ordinary rejection.
 *
 * Rejects with {@link NetworkRequestTimeoutError}, which
 * {@link isTransientConnectivityError} classifies as transient, so auth callers
 * keep the session instead of signing the user out.
 */
export function withDeadline<T>(promise: PromiseLike<T>, timeoutMs: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const deadline = new Promise<never>((_, reject) => {
    timer = globalThis.setTimeout(() => reject(new NetworkRequestTimeoutError(timeoutMs)), timeoutMs);
  });

  // `Promise.race` subscribes to both inputs, so if the original promise settles
  // (or rejects) after the deadline wins, its result is already handled and does
  // not surface as an unhandled rejection.
  return Promise.race([Promise.resolve(promise), deadline]).finally(() => {
    if (timer !== undefined) globalThis.clearTimeout(timer);
  });
}
